<script>
    import { createEventDispatcher, onMount } from 'svelte';
    import { claimsStore } from '../../stores/editorsStore.js';
    import { backendService } from '../../services/backendService.js';
    import AccessibleModal from '../shared/AccessibleModal.svelte';
    // D3 is loaded as external dependency via CDN in index.html
    // Rollup treats it as external (not bundled) due to ES module issues
    import * as d3 from 'd3';

    export let show = false;
    
    const dispatch = createEventDispatcher();
    
    let treeData = [];
    let loading = false;
    let error = null;
    let hoveredClaim = null;
    let tooltipPosition = { x: 0, y: 0 };
    let claimsMap = {}; // Store the claims text map
    
    $: if (show) {
        generateClaimTrees();
    }
    
    async function generateClaimTrees() {
        loading = true;
        error = null;
        
        try {
            const claimsContent = $claimsStore.content;
            if (!claimsContent || !claimsContent.trim()) {
                throw new Error('No claims content available');
            }
            
            console.log('Detecting claim references for tree (lightweight):', claimsContent);
            
            // Use the lightweight detectClaimReferences method - NO TERMS ANALYSIS
            const response = await backendService.detectClaimReferences(claimsContent);
            
            console.log('Response from lightweight claim detection:', response);
            
            // Store the claims text map from the backend response
            if (response.claims) {
                claimsMap = response.claims;
                console.log('Claims text map:', claimsMap);
            }
            
            // Use backend claim_refs data directly
            console.log('=== USING BACKEND CLAIM REFERENCES (LIGHTWEIGHT) ===');
            const backendClaimRefs = response.claim_refs || {};
            console.log('Backend claim refs:', backendClaimRefs);
            
            // Convert backend format to expected format
            const claimRefs = convertBackendFormat(backendClaimRefs);
            
            if (!claimRefs || Object.keys(claimRefs).length === 0) {
                // If we didn't get claim refs from backend, create simple structure from claims data
                if (response.claims) {
                    console.log('No claim_refs from backend, creating simple structure from claims:', response.claims);
                    const simpleClaimRefs = {};
                    Object.keys(response.claims).forEach(claimNum => {
                        simpleClaimRefs[claimNum] = {
                            references: new Set(),
                            independent: true, // Assume independent for fallback
                            parent_independent_claim: null,
                            claim_refs: []
                        };
                    });
                    console.log('Created simple claim refs:', simpleClaimRefs);
                    treeData = buildTreeStructure(simpleClaimRefs);
                } else {
                    throw new Error(`No claim references found in backend response. Response keys: ${Object.keys(response).join(', ')}`);
                }
            } else {
                treeData = buildTreeStructure(claimRefs);
            }
        } catch (err) {
            console.error('Error generating claim trees:', err);
            error = err.message || 'Failed to generate claim trees';
        } finally {
            loading = false;
        }
    }
    
    function buildTreeStructure(claimRefs) {
        const trees = [];
        const claimNodes = {};
        
        // Create nodes for each claim
        Object.keys(claimRefs).forEach(claimNum => {
            const claimInfo = claimRefs[claimNum];
            // Handle both 'references' (Set) and 'claim_refs' (Array) formats
            let dependencies = [];
            if (claimInfo.references && typeof claimInfo.references[Symbol.iterator] === 'function') {
                dependencies = Array.from(claimInfo.references);
            } else if (claimInfo.claim_refs) {
                dependencies = claimInfo.claim_refs;
            }
            
            claimNodes[claimNum] = {
                id: claimNum,
                number: parseInt(claimNum),
                type: claimInfo.independent ? 'independent' : 'dependent',
                dependencies: dependencies,
                parentClaim: claimInfo.parent_independent_claim,
                children: [],
                position: { x: 0, y: 0 }
            };
        });
        
        console.log('Created claim nodes:', claimNodes);
        
        // Build tree structure - start with independent claims
        Object.values(claimNodes).forEach(node => {
            if (node.type === 'independent') {
                console.log(`Building tree for independent claim ${node.id}`);
                const treeData = buildClaimTree(node, claimNodes);
                console.log(`Tree for claim ${node.id}:`, treeData);
                const layoutResult = calculateTreeLayout(treeData.claims);
                trees.push({
                    root: node,
                    claims: treeData.claims,
                    layout: layoutResult.layout,
                    width: layoutResult.width,
                    height: 400
                });
            }
        });
        
        return trees;
    }
    
    function buildClaimTree(root, allNodes) {
        console.log(`\nBuilding tree for root claim ${root.id}:`);
        const treeNodes = [root];
        const processed = new Set([root.id]);
        
        // Function to find direct children of given parent claims
        const findDirectChildren = (parentIds) => {
            console.log(`  Looking for direct children of claims: ${parentIds}`);
            const children = [];
            
            Object.values(allNodes).forEach(node => {
                if (processed.has(node.id)) return;
                
                // Check if this claim's FIRST reference is to one of the parent claims
                const firstReference = node.dependencies && node.dependencies.length > 0 ? 
                    node.dependencies[0].toString() : null;
                
                console.log(`    Claim ${node.id}: first ref = ${firstReference}, all refs = [${node.dependencies}]`);
                
                if (firstReference && parentIds.includes(firstReference)) {
                    console.log(`      -> Adding claim ${node.id} as child of ${firstReference}`);
                    children.push(node);
                    treeNodes.push(node);
                    processed.add(node.id);
                }
            });
            
            console.log(`  Found ${children.length} direct children:`, children.map(c => c.id));
            return children;
        };
        
        // Build tree level by level
        let currentLevel = [root.id];
        while (currentLevel.length > 0) {
            const nextLevelNodes = findDirectChildren(currentLevel);
            currentLevel = nextLevelNodes.map(n => n.id);
        }
        
        // Sort by claim number for consistent display
        const sortedNodes = treeNodes.sort((a, b) => a.number - b.number);
        console.log(`Final tree for claim ${root.id}:`, sortedNodes.map(n => n.id));
        
        return { claims: sortedNodes };
    }
    
    function calculateTreeLayout(claims) {
        console.log('Calculating D3 layout for claims:', claims.map(c => `${c.id}(deps:[${c.dependencies}])`));
        
        // Find the root (independent claim)
        const root = claims.find(c => c.type === 'independent');
        if (!root) return { layout: [], width: 800 };
        
        // Build the tree structure for D3
        function buildD3TreeData(rootNode, allNodes) {
            const nodeMap = new Map(allNodes.map(node => [node.id, node]));
            
            function addChildren(node) {
                const children = allNodes.filter(n => 
                    n.dependencies && n.dependencies.length > 0 && 
                    n.dependencies[0].toString() === node.id
                );
                
                if (children.length > 0) {
                    // Sort children by claim number for consistent ordering
                    children.sort((a, b) => a.number - b.number);
                    node.children = children.map(child => addChildren(child));
                }
                
                return node;
            }
            
            return addChildren({...rootNode});
        }
        
        const treeData = buildD3TreeData(root, claims);
        console.log('D3 tree data:', treeData);
        
        // Create D3 hierarchy
        const rootHierarchy = d3.hierarchy(treeData);
        
        // Calculate tree dimensions - consistent layout
        const fixedHeight = 400; // Fixed height for all trees
        const leafCount = rootHierarchy.leaves().length;
        
        // Calculate width needed for tree, but keep layout consistent
        const nodeSpacing = 80; // Consistent node spacing
        const treeWidth = Math.max(800, leafCount * nodeSpacing + 200);
        
        // Create D3 tree layout - use full calculated width
        const treeLayout = d3.tree()
            .size([treeWidth - 100, fixedHeight - 100]); // Full width, fixed height
        
        // Generate the tree layout
        const treeNodes = treeLayout(rootHierarchy);
        
        // Convert D3 nodes back to our format, adjusting coordinates
        const layout = [];
        treeNodes.descendants().forEach(d3Node => {
            layout.push({
                ...d3Node.data, // Original claim data
                x: d3Node.x + 50, // Add left margin
                y: d3Node.y + 50, // Add top margin
                level: d3Node.depth
            });
        });
        
        console.log('D3 final layout:', layout.map(l => `${l.id}: (${l.x}, ${l.y})`));
        console.log(`Tree dimensions: ${treeWidth}x${fixedHeight}`);
        
        return { layout, width: treeWidth, height: fixedHeight };
    }
    
    function convertBackendFormat(backendClaimRefs) {
        console.log('Converting backend format:', backendClaimRefs);
        
        const convertedRefs = {};
        
        for (const [claimNum, claimInfo] of Object.entries(backendClaimRefs)) {
            // Handle the serialized format from serialize_claim_refs which includes a 'references' array
            let allReferences = new Set();
            
            // Handle serialized format from serialize_claim_refs
            if (claimInfo.references && Array.isArray(claimInfo.references)) {
                // New serialized format has references as array
                allReferences = new Set(claimInfo.references.map(r => r.toString()));
            } else if (claimInfo.claim_refs && Array.isArray(claimInfo.claim_refs)) {
                // Direct claim_refs array
                allReferences = new Set(claimInfo.claim_refs.map(r => r.toString()));
            } else if (claimInfo.references) {
                // Handle Set string representation like "set()" or "{1, 2, 3}"
                const refStr = claimInfo.references.toString();
                if (refStr === 'set()' || refStr === '{}') {
                    allReferences = new Set();
                } else {
                    // Extract numbers from string representation
                    const matches = refStr.match(/\d+/g);
                    if (matches) {
                        allReferences = new Set(matches);
                    }
                }
            }
            
            // For tree visualization, only keep the smallest reference (primary dependency)
            let finalReferences = [];
            if (allReferences.size > 0) {
                const refNumbers = Array.from(allReferences).map(r => parseInt(r));
                const minRef = Math.min(...refNumbers);
                finalReferences = [minRef.toString()];
            }
            
            convertedRefs[claimNum] = {
                references: new Set(finalReferences),
                independent: claimInfo.independent,
                parent_independent_claim: claimInfo.parent_independent_claim,
                claim_refs: finalReferences
            };
        }
        
        console.log('Converted claim refs:', convertedRefs);
        return convertedRefs;
    }
    
    function getNodeClass(node) {
        return `claim-node ${node.type}`;
    }
    
    function close() {
        dispatch('close');
    }
    
    function handleModalClick(e) {
        if (e.target === e.currentTarget) {
            close();
        }
    }
</script>

<AccessibleModal 
    {show} 
    title="Claim Reference Trees" 
    variant="claims"
    on:close={close}
>
    {#if loading}
        <div class="loading">
            <div class="spinner"></div>
            <p>Analyzing claim references...</p>
        </div>
    {:else if error}
        <div class="error">
            <p>{error}</p>
            <button class="modal-button modal-button-accent" on:click={generateClaimTrees}>Try Again</button>
        </div>
    {:else if treeData.length === 0}
        <div class="empty">
            <p>No claim references found. Please add some claims first.</p>
        </div>
    {:else}
        <!-- Debug Information -->
        <!-- <div class="debug-info">
            <h3>Debug: Raw Claim Structure</h3>
            <div class="debug-content">
                <h4>Claims Content Length: {$claimsStore.content ? $claimsStore.content.length : 0} characters</h4>
                
                <h4>ACTUAL CLAIMS TEXT:</h4>
                <pre style="max-height: 200px; overflow-y: auto; background: #f5f5f5; padding: 10px;">{$claimsStore.content}</pre>
                
                <h4>Tree Data Structure:</h4>
                <pre>{JSON.stringify(treeData, null, 2)}</pre>
                
                <h4>Individual Claim Analysis:</h4>
                {#each treeData as tree}
                    {#each tree.claims as claim}
                        <div class="claim-debug">
                            <strong>Claim {claim.number}:</strong>
                            <ul>
                                <li>Type: {claim.type}</li>
                                <li>Dependencies: {JSON.stringify(claim.dependencies)}</li>
                                <li>Parent Claim: {claim.parentClaim || 'none'}</li>
                            </ul>
                        </div>
                    {/each}
                {/each}
            </div>
        </div> -->
        
        <div class="trees-container">
            {#each treeData as tree}
                <div class="tree">
                    <h3>{tree.root ? `Independent Claim ${tree.root.number}` : 'Claim Dependencies'}</h3>
                    <div class="tree-visualization">
                        <svg class="tree-svg" viewBox="0 0 {tree.width} 400" width="{tree.width}" height="400" style="height: 400px !important;">
                            <!-- Draw lines first (so they appear behind nodes) -->
                            {#each tree.layout as claim}
                                {#if claim.dependencies && claim.dependencies.length > 0}
                                    {#each claim.dependencies as depNum}
                                        {#each tree.layout as depClaim}
                                            {#if depClaim.number === parseInt(depNum) || depClaim.id === depNum.toString()}
                                                <!-- Line from dependency to dependent -->
                                                <line 
                                                    x1="{depClaim.x + 30}" 
                                                    y1="{depClaim.y + 55}"
                                                    x2="{claim.x + 30}" 
                                                    y2="{claim.y + 5}"
                                                    class="dependency-line"
                                                />
                                            {/if}
                                        {/each}
                                    {/each}
                                {/if}
                            {/each}
                            
                            <!-- Draw claim nodes -->
                            {#each tree.layout as claim}
                                <g class="claim-group" transform="translate({claim.x}, {claim.y})"
                                   on:mouseenter={(e) => {
                                       hoveredClaim = claim.number;
                                       const rect = e.currentTarget.getBoundingClientRect();
                                       tooltipPosition = { x: rect.left + 30, y: rect.top - 10 };
                                   }}
                                   on:mouseleave={() => hoveredClaim = null}>
                                    <circle 
                                        cx="30" 
                                        cy="30" 
                                        r="25" 
                                        class="claim-circle {claim.type}"
                                    />
                                    <text 
                                        x="30" 
                                        y="30" 
                                        text-anchor="middle" 
                                        dominant-baseline="central" 
                                        class="claim-text"
                                    >
                                        {claim.number}
                                    </text>
                                    
                                </g>
                            {/each}
                        </svg>
                    </div>
                    
                </div>
            {/each}
        </div>
    {/if}
    
    <!-- Tooltip for claim text -->
    {#if hoveredClaim && claimsMap[hoveredClaim]}
        <div class="claim-tooltip" 
             style="position: fixed; top: {tooltipPosition.y}px; left: {tooltipPosition.x}px;">
            <strong>Claim {hoveredClaim}:</strong>
            <div class="claim-tooltip-text">{claimsMap[hoveredClaim]}</div>
        </div>
    {/if}
</AccessibleModal>

<style>
.loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--spacing-4);
    padding: var(--spacing-8);
    color: var(--color-text-secondary);
}

.spinner {
    width: 32px;
    height: 32px;
    border: 3px solid var(--color-border-light);
    border-radius: 50%;
    border-top: 3px solid var(--color-action-primary);
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.error, .empty {
    padding: var(--spacing-8);
    text-align: center;
    color: var(--color-text-secondary);
}

.error .modal-button {
    margin-top: var(--spacing-4);
}

.trees-container {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-6);

    /* Remove nested scrolling - let modal handle overall scrolling */
}

.tree {
    padding: var(--spacing-6);
    background: var(--color-interactive-default);
    border: var(--border-default);
    border-radius: var(--border-radius-xl);
    box-shadow: var(--shadow-sm);
    transition: all var(--duration-fast) var(--ease-in-out);
}

.tree:hover {
    box-shadow: var(--shadow-md);
    transform: translateY(-1px);
}

.tree h3 {
    padding-bottom: var(--spacing-2);
    margin: 0 0 var(--spacing-4) 0;
    font-size: var(--font-size-lg);
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-primary);
    border-bottom: var(--border-default);
}

.tree-visualization {
    /* Center the tree content */
    display: flex;
    justify-content: center;
    align-items: center;

    /* Fixed height container for consistent tree display */
    height: 400px;
    margin: var(--spacing-4) 0;
    overflow: auto hidden;

    /* Always show horizontal scrollbar when needed */
    scrollbar-gutter: stable;
}

/* Force horizontal scrollbar to always be visible */
.tree-visualization::-webkit-scrollbar:horizontal {
    height: 12px;
    background-color: var(--color-bg-secondary);
    border-radius: 6px;
}

.tree-visualization::-webkit-scrollbar-thumb:horizontal {
    background-color: var(--color-border-default);
    border: 2px solid var(--color-bg-secondary);
    border-radius: 6px;
}

.tree-visualization::-webkit-scrollbar-thumb:horizontal:hover {
    background-color: var(--color-text-secondary);
}

/* Vertical scrollbar only when needed */
.tree-visualization::-webkit-scrollbar:vertical {
    width: 12px;
    background-color: var(--color-bg-secondary);
    border-radius: 6px;
}

.tree-visualization::-webkit-scrollbar-thumb:vertical {
    background-color: var(--color-border-default);
    border: 2px solid var(--color-bg-secondary);
    border-radius: 6px;
}

.tree-visualization::-webkit-scrollbar-thumb:vertical:hover {
    background-color: var(--color-text-secondary);
}

.tree-svg {
    display: block;

    /* Prevent browser scaling */
    flex-shrink: 0;

    /* Maintain actual size - no scaling */
    height: 400px !important;
    background: var(--color-bg-primary);
    border: var(--border-default);
    border-radius: var(--border-radius-lg);
    box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.05);
}

.claim-circle {
    stroke-width: 2;
    transition: all 0.3s ease;
    cursor: pointer;
    fill: var(--color-btn-default) !important;
    stroke: var(--color-text-primary) !important;
}

.claim-circle.independent {
    fill: var(--color-btn-default) !important;
    stroke: var(--color-text-primary) !important;
}

.claim-circle.dependent {
    fill: var(--color-btn-default) !important;
    stroke: var(--color-text-primary) !important;
}

.claim-circle:hover {
    stroke-width: 3;
    filter: brightness(0.95);
}

.claim-text {
    font-size: 16px;
    font-weight: var(--font-weight-semibold);
    fill: var(--color-text-primary);
    pointer-events: none;
}

.claim-type-text {
    font-size: 12px;
    fill: var(--color-text-secondary);
    pointer-events: none;
}

.dependency-line {
    stroke: var(--color-text-secondary);
    stroke-width: 2;
    fill: none;
    opacity: 0.8;
    transition: opacity 0.3s ease;
}

.dependency-line:hover {
    opacity: 1;
    stroke-width: 3;
}

.debug-info {
    max-height: 400px;
    padding: var(--spacing-4);
    margin-bottom: var(--spacing-6);
    overflow-y: auto;
    background: var(--color-bg-secondary);
    border: var(--border-default);
    border-radius: var(--border-radius-lg);
}

.debug-content pre {
    padding: var(--spacing-3);
    overflow-x: auto;
    font-size: var(--font-size-xs);
    white-space: pre-wrap;
    background: var(--color-bg-primary);
    border-radius: var(--border-radius-md);
}

.claim-debug {
    padding: var(--spacing-2);
    margin: var(--spacing-2) 0;
    background: var(--color-bg-primary);
    border-radius: var(--border-radius-sm);
}

.claim-debug ul {
    margin: var(--spacing-1) 0 0 var(--spacing-4);
    list-style-type: disc;
}

.claim-debug li {
    margin: var(--spacing-1) 0;
    font-size: var(--font-size-sm);
}

.claim-tooltip {
    position: fixed;
    z-index: 10000;
    max-width: 500px;
    padding: var(--spacing-3);
    background: var(--color-bg-primary);
    background-color: rgba(255, 255, 255, 0.98);
    border: var(--border-default);
    border-radius: var(--border-radius-lg);
    opacity: 1;
    box-shadow: var(--shadow-lg);
    transform: translateY(-100%) translateX(-50%);
    pointer-events: none;
    backdrop-filter: blur(4px);
}

.claim-tooltip strong {
    display: block;
    margin-bottom: var(--spacing-2);
    font-size: var(--font-size-sm);
    color: var(--color-text-primary);
}

.claim-tooltip-text {
    max-height: 200px;
    overflow-y: auto;
    font-size: var(--font-size-xs);
    line-height: 1.5;
    color: var(--color-text-secondary);
}

.claim-group {
    cursor: pointer;
}


</style>